/**
 * Calcula juros compostos com aportes mensais
 * @param {number} initialCapital - Capital inicial
 * @param {number} monthlyContribution - Aporte mensal
 * @param {number} monthlyRate - Taxa de juros mensal (em decimal)
 * @param {number} period - Período em meses
 * @param {number} taxRate - Alíquota de imposto (em decimal)
 * @returns {Array} Array de resultados mensais
 */
export const calculateCompoundInterest = (
  initialCapital,
  monthlyContribution,
  monthlyRate,
  period,
  taxRate = 0
) => {
  // Para períodos muito longos, podemos usar uma abordagem de amostragem
  // para melhorar a performance, especialmente em dispositivos móveis
  const samplingRate = period > 120 ? Math.ceil(period / 120) : 1;
  const results = [];
  
  let totalAmount = initialCapital;
  let totalCapital = initialCapital;
  let monthlyInterest = 0;
  let totalInterest = 0;
  
  // Pré-alocar para Arrays de resultados grandes melhora a performance
  results.length = Math.ceil(period / samplingRate) + 1;
  
  // Usar uma única fórmula para calcular juros compostos com aportes mensais
  // é mais eficiente para períodos longos sem precisar de resultados intermediários
  if (period > 600 && samplingRate > 10) {
    // Fórmula direta para juros compostos com aportes regulares:
    // M = P(1+i)^n + PMT×[ ((1+i)^n - 1) / i ]
    const finalAmount = initialCapital * Math.pow(1 + monthlyRate, period) + 
                        monthlyContribution * (Math.pow(1 + monthlyRate, period) - 1) / monthlyRate;
    
    const finalCapital = initialCapital + (monthlyContribution * period);
    const finalInterest = finalAmount - finalCapital;
    const finalTax = finalInterest * taxRate;
    
    // Gerar apenas pontos de amostragem para o gráfico
    for (let month = 0; month <= period; month += samplingRate) {
      if (month === 0) {
        results[0] = {
          month: 0,
          monthlyInterest: 0,
          totalInterest: 0,
          totalCapital,
          totalAmount,
          returnRate: 0,
          tax: 0,
          netAmount: totalAmount
        };
      } else if (month === period) {
        // Usar valores calculados pela fórmula direta para o resultado final
        const returnRate = totalCapital > 0 ? (finalInterest / finalCapital) * 100 : 0;
        results[results.length - 1] = {
          month: period,
          monthlyInterest: finalInterest / period, // Média
          totalInterest: finalInterest,
          totalCapital: finalCapital,
          totalAmount: finalAmount,
          returnRate,
          tax: finalTax,
          netAmount: finalAmount - finalTax
        };
      } else {
        // Interpolar valores intermediários para o gráfico
        const progress = month / period;
        const currentAmount = initialCapital * Math.pow(1 + monthlyRate, month) + 
                              monthlyContribution * (Math.pow(1 + monthlyRate, month) - 1) / monthlyRate;
        const currentCapital = initialCapital + (monthlyContribution * month);
        const currentInterest = currentAmount - currentCapital;
        const currentTax = currentInterest * taxRate;
        const returnRate = currentCapital > 0 ? (currentInterest / currentCapital) * 100 : 0;
        
        results[Math.floor(month / samplingRate)] = {
          month,
          monthlyInterest: currentInterest / month, // Média
          totalInterest: currentInterest,
          totalCapital: currentCapital,
          totalAmount: currentAmount,
          returnRate,
          tax: currentTax,
          netAmount: currentAmount - currentTax
        };
      }
    }
    
    return results.filter(item => item); // Remove undefined items
  }
  
  // Abordagem iterativa original para períodos menores
  for (let month = 0; month <= period; month++) {
    // Só armazenar pontos de acordo com a taxa de amostragem
    if (month % samplingRate === 0 || month === period) {
      if (month === 0) {
        // Mês inicial - apenas o capital inicial
        results[Math.floor(month / samplingRate)] = {
          month,
          monthlyInterest: 0,
          totalInterest: 0,
          totalCapital,
          totalAmount,
          returnRate: 0,
          tax: 0,
          netAmount: totalAmount
        };
      } else {
        // Aplica juros sobre o montante atual
        monthlyInterest = totalAmount * monthlyRate;
        totalInterest += monthlyInterest;
        
        // Adiciona o aporte mensal ao capital e ao montante
        totalAmount += monthlyInterest + monthlyContribution;
        totalCapital += monthlyContribution;
        
        // Calcula o imposto sobre os juros (se aplicável)
        const tax = totalInterest * taxRate;
        const netAmount = totalAmount - tax;
        
        // Calcula a taxa de retorno
        const returnRate = totalCapital > 0 ? (totalInterest / totalCapital) * 100 : 0;
        
        results[Math.floor(month / samplingRate)] = {
          month,
          monthlyInterest,
          totalInterest,
          totalCapital,
          totalAmount,
          returnRate,
          tax,
          netAmount
        };
      }
    } else {
      // Para meses que não são armazenados, ainda calculamos para manter a acurácia
      if (month > 0) {
        monthlyInterest = totalAmount * monthlyRate;
        totalInterest += monthlyInterest;
        totalAmount += monthlyInterest + monthlyContribution;
        totalCapital += monthlyContribution;
      }
    }
  }
  
  return results.filter(item => item); // Remove undefined items
};

/**
 * Calcula o tempo necessário para atingir um valor alvo
 * @param {number} initialCapital - Capital inicial
 * @param {number} monthlyContribution - Aporte mensal
 * @param {number} monthlyRate - Taxa de juros mensal (em decimal)
 * @param {number} targetValue - Valor alvo a ser atingido
 * @param {number} maxMonths - Número máximo de meses a considerar
 * @returns {Object} Resultado do cálculo
 */
export const calculateTimeToTarget = (
  initialCapital,
  monthlyContribution,
  monthlyRate,
  targetValue,
  maxMonths = 1200 // 100 anos como limite
) => {
  // Caso especial: se o valor alvo já foi atingido
  if (initialCapital >= targetValue) {
    return {
      months: 0,
      years: 0,
      amount: initialCapital,
      targetReached: true
    };
  }
  
  // Caso especial: taxa de juros zero ou negativa
  if (monthlyRate <= 0) {
    // Sem juros, apenas contribuições
    if (monthlyContribution <= 0) {
      return {
        months: maxMonths,
        years: maxMonths / 12,
        amount: initialCapital,
        targetReached: false
      };
    }
    
    const monthsNeeded = Math.ceil((targetValue - initialCapital) / monthlyContribution);
    if (monthsNeeded > maxMonths) {
      return {
        months: maxMonths,
        years: maxMonths / 12,
        amount: initialCapital + (monthlyContribution * maxMonths),
        targetReached: false
      };
    }
    
    return {
      months: monthsNeeded,
      years: monthsNeeded / 12,
      amount: initialCapital + (monthlyContribution * monthsNeeded),
      targetReached: true
    };
  }
  
  // Usar fórmula direta para calcular o tempo necessário
  // ao invés de simulação iterativa
  if (monthlyContribution > 0) {
    // Fórmula: log((targetValue * rate + contribution) / (initialCapital * rate + contribution)) / log(1 + rate)
    try {
      const numerator = targetValue * monthlyRate + monthlyContribution;
      const denominator = initialCapital * monthlyRate + monthlyContribution;
      
      if (denominator <= 0) {
        // Fallback para o método iterativo se a fórmula não for aplicável
        return calculateTimeToTargetIterative(initialCapital, monthlyContribution, monthlyRate, targetValue, maxMonths);
      }
      
      const months = Math.ceil(Math.log(numerator / denominator) / Math.log(1 + monthlyRate));
      
      if (months <= 0 || !isFinite(months)) {
        // Fallback para o método iterativo se o resultado for inválido
        return calculateTimeToTargetIterative(initialCapital, monthlyContribution, monthlyRate, targetValue, maxMonths);
      }
      
      if (months > maxMonths) {
        return {
          months: maxMonths,
          years: maxMonths / 12,
          amount: calculateFinalAmountFormula(initialCapital, monthlyContribution, monthlyRate, maxMonths),
          targetReached: false
        };
      }
      
      return {
        months,
        years: months / 12,
        amount: calculateFinalAmountFormula(initialCapital, monthlyContribution, monthlyRate, months),
        targetReached: true
      };
    } catch (error) {
      console.warn('Erro ao calcular com fórmula direta, usando método iterativo', error);
      return calculateTimeToTargetIterative(initialCapital, monthlyContribution, monthlyRate, targetValue, maxMonths);
    }
  } else {
    // Apenas juros compostos sem aportes mensais
    // Fórmula: log(targetValue / initialCapital) / log(1 + rate)
    try {
      const months = Math.ceil(Math.log(targetValue / initialCapital) / Math.log(1 + monthlyRate));
      
      if (months <= 0 || !isFinite(months)) {
        return calculateTimeToTargetIterative(initialCapital, monthlyContribution, monthlyRate, targetValue, maxMonths);
      }
      
      if (months > maxMonths) {
        return {
          months: maxMonths,
          years: maxMonths / 12,
          amount: initialCapital * Math.pow(1 + monthlyRate, maxMonths),
          targetReached: false
        };
      }
      
      return {
        months,
        years: months / 12,
        amount: initialCapital * Math.pow(1 + monthlyRate, months),
        targetReached: true
      };
    } catch (error) {
      console.warn('Erro ao calcular com fórmula direta, usando método iterativo', error);
      return calculateTimeToTargetIterative(initialCapital, monthlyContribution, monthlyRate, targetValue, maxMonths);
    }
  }
};

/**
 * Método iterativo para calcular o tempo para atingir um valor alvo
 * Usado como fallback
 */
const calculateTimeToTargetIterative = (
  initialCapital,
  monthlyContribution,
  monthlyRate,
  targetValue,
  maxMonths
) => {
  let amount = initialCapital;
  let months = 0;
  
  while (amount < targetValue && months < maxMonths) {
    amount = amount * (1 + monthlyRate) + monthlyContribution;
    months++;
  }
  
  return {
    months,
    years: months / 12,
    amount,
    targetReached: amount >= targetValue
  };
};

/**
 * Calcula o montante final com fórmula direta
 */
const calculateFinalAmountFormula = (initialCapital, monthlyContribution, monthlyRate, months) => {
  return initialCapital * Math.pow(1 + monthlyRate, months) + 
         monthlyContribution * (Math.pow(1 + monthlyRate, months) - 1) / monthlyRate;
};

/**
 * Calcula diferentes cenários de investimento
 * @param {number} initialCapital - Capital inicial
 * @param {number} monthlyContribution - Aporte mensal
 * @param {number} period - Período em meses
 * @param {Array} scenarios - Array de cenários com taxas de juros
 * @returns {Array} Array de resultados por cenário
 */
export const calculateScenarios = (
  initialCapital,
  monthlyContribution,
  period,
  scenarios
) => {
  return scenarios.map(scenario => {
    const monthlyRate = scenario.interestRate / 100;
    
    // Usar fórmula direta para calcular o resultado final
    // M = P(1+i)^n + PMT×[ ((1+i)^n - 1) / i ]
    const totalAmount = initialCapital * Math.pow(1 + monthlyRate, period) + 
                        monthlyContribution * (Math.pow(1 + monthlyRate, period) - 1) / monthlyRate;
    
    const totalCapital = initialCapital + (monthlyContribution * period);
    const totalInterest = totalAmount - totalCapital;
    const returnRate = (totalInterest / totalCapital) * 100;
    
    return {
      name: scenario.name,
      interestRate: scenario.interestRate,
      totalAmount,
      totalCapital,
      totalInterest,
      returnRate,
      color: scenario.color,
    };
  });
};